import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { getMyCredits } from "@/lib/account.functions";
import { getDealDetail, listMyDeals, type DealSummary } from "@/lib/deals.functions";
import {
  buildDealPlan,
  currentWeekFor,
  getDealPlan,
  setDealPlanItemDone,
  type DealPlanItem,
  type DealPlanWeek,
} from "@/lib/deal-plans.functions";
import { listDealNextStepInputs } from "@/lib/agent-actions.functions";
import { DealPageHeader } from "@/components/deal-page-header";
import { DealNav } from "@/components/deal-nav";
import { RankingDemoBanner } from "@/components/ranking-demo-banner";
import { RecommendedNextSteps } from "@/components/dashboard/deal-card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";


export const Route = createFileRoute("/_authed/deals/$dealId_/plan")({
  head: () => ({
    meta: [
      { title: "Deal plan: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: DealPlanPage,
});

const DAY_MS = 86400000;
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** "Sep 6" style short date, parsed from an ISO or date-only string. */
function shortDate(value: string): string {
  const d = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(d.getTime())) return value;
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function addDays(dateOnly: string, days: number): string {
  const base = Date.parse(`${dateOnly.slice(0, 10)}T00:00:00Z`);
  return new Date(base + days * DAY_MS).toISOString().slice(0, 10);
}

function weekRange(startDate: string, week: number): { from: string; to: string } {
  return {
    from: shortDate(addDays(startDate, 7 * (week - 1))),
    to: shortDate(addDays(startDate, 7 * week - 1)),
  };
}

function DealPlanPage() {
  const { dealId } = Route.useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fetchDetail = useServerFn(getDealDetail);
  const fetchPlan = useServerFn(getDealPlan);
  const callBuild = useServerFn(buildDealPlan);
  const callSetDone = useServerFn(setDealPlanItemDone);

  const detail = useQuery({
    queryKey: ["deal-detail", dealId],
    queryFn: () => fetchDetail({ data: { dealId } }),
  });
  const rankingDemo =
    (detail.data as { ranking_demo?: { category: string; stage: string } | null } | undefined)
      ?.ranking_demo ?? null;

  const fetchCredits = useServerFn(getMyCredits);
  const credits = useQuery({
    queryKey: ["my-credits", user?.id],
    queryFn: () => fetchCredits(),
    enabled: !!user,
  });

  const fetchDeals = useServerFn(listMyDeals);
  const deals = useQuery({
    queryKey: ["my-recent-deals", user?.id],
    queryFn: () => fetchDeals({ data: {} }),
    enabled: !!user,
  });
  const deal = React.useMemo(
    () => (deals.data ?? []).find((d) => d.id === dealId) as DealSummary | undefined,
    [deals.data, dealId],
  );

  const fetchNextStepInputs = useServerFn(listDealNextStepInputs);
  const nextStepInputs = useQuery({
    queryKey: ["deal-next-step-inputs", user?.id],
    queryFn: () => fetchNextStepInputs({}),
    enabled: !!user,
    staleTime: 30_000,
  });

  const planQuery = useQuery({
    queryKey: ["deal-plan", dealId],
    queryFn: () => fetchPlan({ data: { deal_id: dealId } }),
  });
  const plan = planQuery.data?.plan ?? null;


  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["deal-plan", dealId] });
    queryClient.invalidateQueries({ queryKey: ["deal-detail", dealId] });
    queryClient.invalidateQueries({ queryKey: ["my-recent-deals"] });
    queryClient.invalidateQueries({ queryKey: ["deal-plan-summaries"] });
  };

  const buildMutation = useMutation({
    mutationFn: (vars: { refresh?: boolean }) =>
      callBuild({ data: { deal_id: dealId, refresh: vars.refresh } }),
    onSuccess: invalidate,
  });

  const doneMutation = useMutation({
    mutationFn: (vars: { item_id: string; done: boolean }) =>
      callSetDone({
        data: { deal_id: dealId, item_id: vars.item_id, done: vars.done },
      }),
    onSuccess: invalidate,
  });

  const markWeekComplete = async (week: DealPlanWeek) => {
    for (const item of week.items) {
      if (item.done) continue;
      await doneMutation.mutateAsync({ item_id: item.id, done: true });
    }
  };

  const generating =
    buildMutation.isPending || plan?.status === "generating";
  const showFailed = !generating && plan?.status === "failed";
  const showReady = !generating && plan?.status === "ready";
  const showEmpty = !generating && !plan && !planQuery.isLoading;

  const currentWeek = plan ? currentWeekFor(plan.start_date) : 1;

  return (
    <main className="mx-auto max-w-6xl px-6 py-10 space-y-6">
      {rankingDemo && (
        <RankingDemoBanner
          category={rankingDemo.category}
          stage={rankingDemo.stage}
        />
      )}
      <DealPageHeader dealId={dealId} />
      <DealNav
        dealId={dealId}
        active="plan"
        isDemo={(detail.data?.deal as { is_demo?: boolean } | undefined)?.is_demo === true}
      />

      {deal && (
        <RecommendedNextSteps
          deal={deal}
          nextStepInputs={nextStepInputs.data?.byDealId?.[dealId] ?? null}
          creditsRemaining={credits.data?.credits_remaining ?? 0}
          freeIrrRemaining={credits.data?.free_deck_studio_reports_remaining ?? 0}
          hideEmptyState
        />
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">

        <div>
          <h2 className="font-serif text-2xl text-brand-ink">30-day plan</h2>
          {showReady && plan && (
            <p className="mt-1 text-[14px] text-brand-muted">
              Built {shortDate(plan.generated_at)} from your contacts, reports,
              and goal.
            </p>
          )}
          {showReady && plan?.source_raise_strategy_report_id && (
            <p className="mt-1 text-[14px] text-brand-muted">
              Built from the Raise Strategy Report.
            </p>
          )}
        </div>
        {showReady && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => buildMutation.mutate({ refresh: true })}
            disabled={buildMutation.isPending}
          >
            Refresh plan
          </Button>
        )}
      </div>

      {showEmpty && (
        <div className="space-y-4 rounded-lg border border-brand-line bg-brand-paper-2 p-6">
          <p className="text-[14px] text-brand-muted">
            No plan yet. Build one from this deal&apos;s contacts, reports, and
            goal.
          </p>
          <Button
            type="button"
            onClick={() => buildMutation.mutate({})}
            disabled={buildMutation.isPending}
          >
            Build my 30-day plan
          </Button>
        </div>
      )}

      {generating && (
        <div className="rounded-lg border border-brand-line bg-brand-paper-2 p-6 text-[14px] text-brand-muted">
          <Loader2 className="mr-2 inline size-4 animate-spin" />
          Building your plan.
        </div>
      )}

      {showFailed && (
        <div className="space-y-4 rounded-lg border border-brand-line bg-brand-paper-2 p-6">
          <p className="text-[14px] text-brand-muted">
            The plan could not be built. Try again.
          </p>
          <Button
            type="button"
            onClick={() => buildMutation.mutate({ refresh: false })}
            disabled={buildMutation.isPending}
          >
            Try again
          </Button>
        </div>
      )}

      {showReady && plan && (
        <div className="space-y-4">
          {plan.weeks.map((week) => {
            const range = weekRange(plan.start_date, week.week);
            const isCurrent = week.week === currentWeek;
            const isPast = week.week < currentWeek;
            return (
              <section
                key={week.week}
                className={cn(
                  "rounded-lg border border-brand-line bg-brand-paper p-4 sm:p-5",
                  isCurrent && "border-l-4 border-l-brand-accent",
                  isPast && "opacity-70",
                )}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-[15px] font-semibold text-brand-ink">
                    Week {week.week} · {range.from} to {range.to}
                  </h3>
                  {isCurrent && (
                    <span className="rounded-full bg-brand-accent/15 px-2 py-0.5 text-[11px] font-medium text-brand-ink">
                      This week
                    </span>
                  )}
                </div>
                <p className="mt-1 text-[14px] text-brand-muted">
                  {week.focus}
                </p>
                <ul className="mt-3 space-y-2">
                  {week.items.map((item: DealPlanItem) => (
                    <li key={item.id} className="flex items-start gap-2.5">
                      <Checkbox
                        id={item.id}
                        checked={item.done}
                        disabled={doneMutation.isPending}
                        onCheckedChange={(checked) =>
                          doneMutation.mutate({
                            item_id: item.id,
                            done: checked === true,
                          })
                        }
                        className="mt-0.5 shrink-0"
                      />
                      <label
                        htmlFor={item.id}
                        className={cn(
                          "text-[14px] leading-snug text-brand-ink break-words",
                          item.done && "text-brand-muted line-through",
                        )}
                      >
                        {item.text}
                        {item.done && item.done_at && (
                          <span className="ml-2 text-[12px] text-brand-muted no-underline">
                            {shortDate(item.done_at)}
                          </span>
                        )}
                      </label>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 text-right">
                  <button
                    type="button"
                    onClick={() => markWeekComplete(week)}
                    disabled={doneMutation.isPending}
                    className="text-[13px] text-brand-muted underline-offset-2 hover:text-brand-ink hover:underline disabled:opacity-50"
                  >
                    Mark week complete
                  </button>
                </div>
              </section>
            );
          })}
        </div>
      )}
    </main>
  );
}
